// One visual-viewport source for every interactive mobile surface. The controller
// writes CSS variables directly on <html>; keyboard animation therefore does not
// travel through React and cannot rerender the shell on every viewport pixel.
import { useEffect } from 'react';

export type AppViewportMetrics = {
  innerHeight: number;
  clientHeight: number;
  height: number;
  top: number;
  left: number;
  scale: number;
  baseline: number;
  bottomGap: number;
  keyboardHeight: number;
  keyboard: boolean;
  scrollY: number;
};

const settled = new Set<(metrics: AppViewportMetrics) => void>();
let current: AppViewportMetrics | null = null;
let baseline = 0;
let raf = 0;
let stableFrames = 0;
let lastFrame = '';
let running = false;

const fieldFocused = () => {
  const active = document.activeElement;
  return active instanceof HTMLElement && (
    active.matches('textarea, select, input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="button"]):not([type="submit"])')
    || active.isContentEditable
  );
};

const px = (value: number) => `${Math.max(0, value).toFixed(2)}px`;
const setVar = (name: string, value: string) => {
  const root = document.documentElement;
  if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value);
};

function readMetrics(): AppViewportMetrics {
  const viewport = window.visualViewport;
  const innerHeight = window.innerHeight;
  const clientHeight = document.documentElement.clientHeight;
  const height = viewport?.height ?? innerHeight;
  const top = viewport?.offsetTop ?? 0;
  const left = viewport?.offsetLeft ?? 0;
  const scale = viewport?.scale ?? 1;
  const focused = fieldFocused();

  // Capture the unobscured layout height while no editing control owns focus.
  // Browser chrome may change it by a few pixels; an iOS keyboard is only
  // inferred when a focused field loses a substantial part of that baseline.
  const layout = Math.max(innerHeight, clientHeight, height);
  if (!focused && Math.abs(scale - 1) < 0.02) baseline = layout;
  if (!baseline) baseline = layout;
  const heightLoss = Math.max(0, baseline - height);
  const threshold = Math.max(120, baseline * 0.18);
  const keyboard = focused && Math.abs(scale - 1) < 0.02 && heightLoss > threshold;

  return {
    innerHeight,
    clientHeight,
    height,
    top,
    left,
    scale,
    baseline,
    bottomGap: Math.max(0, baseline - top - height),
    keyboardHeight: keyboard ? heightLoss : 0,
    keyboard,
    scrollY: window.scrollY,
  };
}

function commit(metrics: AppViewportMetrics) {
  current = metrics;
  setVar('--app-vv-height', px(metrics.height));
  setVar('--app-vv-top', px(metrics.top));
  setVar('--app-vv-left', px(metrics.left));
  setVar('--app-vv-bottom-gap', px(metrics.bottomGap));
  setVar('--app-keyboard-height', px(metrics.keyboardHeight));
  document.documentElement.classList.toggle('app-keyboard-open', metrics.keyboard);
}

function frame() {
  raf = 0;
  const metrics = readMetrics();
  const signature = [metrics.height, metrics.top, metrics.left, metrics.scale, metrics.keyboard].map((n) => String(n)).join('|');
  stableFrames = signature === lastFrame ? stableFrames + 1 : 0;
  lastFrame = signature;
  commit(metrics); // all geometry reads above; all style writes happen here
  if (stableFrames < 3) raf = requestAnimationFrame(frame);
  else settled.forEach((listener) => listener(metrics));
}

function schedule() {
  stableFrames = 0;
  if (!raf) raf = requestAnimationFrame(frame);
}

function resetOrientation() {
  baseline = 0;
  schedule();
}

function rect(selector: string) {
  const node = document.querySelector<HTMLElement>(selector);
  if (!node) return null;
  const box = node.getBoundingClientRect();
  return { top: box.top, right: box.right, bottom: box.bottom, left: box.left, width: box.width, height: box.height };
}

function start() {
  if (running) return;
  running = true;
  const viewport = window.visualViewport;
  viewport?.addEventListener('resize', schedule, { passive: true });
  viewport?.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', resetOrientation, { passive: true });
  document.addEventListener('focusin', schedule, true);
  document.addEventListener('focusout', schedule, true);
  schedule();

  // On-demand diagnostics only. No observer, render loop or production UI.
  if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>).__orcaViewportDebug = () => ({
      ...current,
      activeElement: document.activeElement instanceof HTMLElement ? `${document.activeElement.tagName.toLowerCase()}#${document.activeElement.id}.${document.activeElement.className}` : null,
      css: Object.fromEntries(['--app-vv-height', '--app-vv-top', '--app-vv-bottom-gap', '--app-keyboard-height'].map((name) => [name, getComputedStyle(document.documentElement).getPropertyValue(name).trim()])),
      aiShell: rect('body.on-ai .panel'),
      contextualShell: rect('.ask-panel'),
      composer: rect('body.on-ai .cs-cmp:not(.ask-cmp), .ask-panel .ask-cmp'),
      input: rect('body.on-ai #aiInput, .ask-panel input'),
    });
  }
}

function stop() {
  if (!running) return;
  running = false;
  const viewport = window.visualViewport;
  viewport?.removeEventListener('resize', schedule);
  viewport?.removeEventListener('scroll', schedule);
  window.removeEventListener('resize', schedule);
  window.removeEventListener('orientationchange', resetOrientation);
  document.removeEventListener('focusin', schedule, true);
  document.removeEventListener('focusout', schedule, true);
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
  document.documentElement.classList.remove('app-keyboard-open');
}

export const subscribeViewportSettled = (listener: (metrics: AppViewportMetrics) => void) => {
  settled.add(listener);
  return () => settled.delete(listener);
};

export function useVisualViewportController() {
  useEffect(() => {
    start();
    return stop;
  }, []);
}
